/**
 * Utility functions for AppointmentCard component
 */

import { format, isToday, isTomorrow, isYesterday, parseISO, startOfDay } from 'date-fns';

import type { Appointment, DiagnosisInfo, MedicationInfo, QuestionnaireInfo } from '../../types';

/**
 * Format appointment time range
 */
export const formatTimeRange = (start: string, end: string): string => {
  return `${start} - ${end}`;
};

/**
 * Calculate end time from start time and duration
 */
export const calculateEndTime = (startTime: Date | string, duration?: number): Date => {
  const start = new Date(startTime);
  const durationMinutes = duration || 50; // default 50 min appointment
  return new Date(start.getTime() + durationMinutes * 60000);
};

/**
 * Determine appointment state based on current time
 */
export type AppointmentState = 'past' | 'active' | 'upcoming' | 'future' | 'unknown';

export const getAppointmentState = (appointment: Appointment): AppointmentState => {
  if (!appointment.start_time) return 'unknown';

  const now = new Date();
  const start = new Date(appointment.start_time);
  const end = calculateEndTime(appointment.start_time, appointment.duration);

  // Past appointment
  if (end < now) return 'past';

  // Active (15 mins before start to end)
  const activeStart = new Date(start.getTime() - 15 * 60000);
  if (now >= activeStart && now <= end) return 'active';

  // Upcoming (within 2 hours)
  const twoHoursFromNow = new Date(now.getTime() + 2 * 60 * 60000);
  if (start <= twoHoursFromNow) return 'upcoming';

  return 'future';
};

/**
 * Get button configuration based on appointment state
 */
export const getButtonConfig = (state: AppointmentState) => {
  switch (state) {
    case 'past':
      return {
        label: 'View Session',
        variant: 'outlined' as const,
        action: 'view' as const,
      };
    case 'active':
      return {
        label: 'Record Session',
        variant: 'contained' as const,
        action: 'record' as const,
      };
    case 'upcoming':
      return {
        label: 'Record Session',
        variant: 'contained' as const,
        action: 'record' as const,
      };
    case 'future':
      return {
        label: 'Record Session',
        variant: 'outlined' as const,
        action: 'record' as const,
      };
    default:
      return {
        label: 'Record Session',
        variant: 'outlined' as const,
        action: 'record' as const,
      };
  }
};

/**
 * Extract display data from appointment object
 */
export const extractAppointmentData = (appointment: Appointment) => {
  const data: {
    firstName?: string;
    lastName?: string;
    preferredName?: string;
    patientId?: string;
    appointmentTime?: { start: string; end: string };
    appointmentType?: string;
    encounterId?: string;
    encounterNoteTitle?: string;
  } = {};

  // Patient info - now using the new first_name, last_name, and preferred_name fields
  if (appointment.patient) {
    if (appointment.patient.first_name) {
      data.firstName = appointment.patient.first_name;
    }
    if (appointment.patient.last_name) {
      data.lastName = appointment.patient.last_name;
    }
    if (appointment.patient.preferred_name) {
      data.preferredName = appointment.patient.preferred_name;
    }

    // Fall back to name field if first/last names aren't available
    if (!data.firstName && !data.lastName && appointment.patient.name) {
      data.firstName = appointment.patient.name;
      data.lastName = '';
    }

    if (appointment.patient.patient_id) {
      data.patientId = appointment.patient.patient_id;
    }
  }

  // Encounter info
  if (
    appointment.encounter &&
    typeof appointment.encounter === 'object'
  ) {
    const encounter = appointment.encounter as { encounter_id?: string; note_title?: string };
    if (encounter.encounter_id) {
      data.encounterId = encounter.encounter_id;
    }
    if (encounter.note_title) {
      data.encounterNoteTitle = encounter.note_title;
    }
  }

  // Times for display
  if (appointment.start_time) {
    try {
      const start = new Date(appointment.start_time);
      const end = calculateEndTime(appointment.start_time, appointment.duration);

      data.appointmentTime = {
        start: format(start, 'h:mma').toLowerCase(),
        end: format(end, 'h:mma').toLowerCase(),
      };
    } catch (error) {
      console.warn('Failed to parse appointment time:', error);
    }
  }

  // Type
  if (appointment.encounter_type) {
    data.appointmentType = appointment.encounter_type;
  }

  return data;
};

/**
 * Get date group label for appointment grouping
 * Returns "YESTERDAY, Month day, year", "TODAY, Month day, year", "TOMORROW, Month day, year" for relative dates
 * Returns formatted date string for other dates
 */
export const getDateGroupLabel = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isToday(dateObj)) {
    return `TODAY, ${format(dateObj, 'MMMM d, yyyy')}`;
  }

  if (isYesterday(dateObj)) {
    return `YESTERDAY, ${format(dateObj, 'MMMM d, yyyy')}`;
  }

  if (isTomorrow(dateObj)) {
    return `TOMORROW, ${format(dateObj, 'MMMM d, yyyy')}`;
  }

  // Format as "Monday, September 30, 2025"
  return format(dateObj, 'EEEE, MMMM d, yyyy');
};

/**
 * Get date key for grouping appointments
 * Returns ISO date string (YYYY-MM-DD) for consistent grouping
 */
export const getDateGroupKey = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(startOfDay(dateObj), 'yyyy-MM-dd');
};

/**
 * Group appointments by date
 */
export const groupAppointmentsByDate = (
  appointments: Appointment[],
): Map<string, Appointment[]> => {
  const groups = new Map<string, Appointment[]>();

  appointments.forEach(appointment => {
    if (!appointment.start_time) return;

    const dateKey = getDateGroupKey(appointment.start_time);
    const existing = groups.get(dateKey) || [];
    groups.set(dateKey, [...existing, appointment]);
  });

  return groups;
};

/**
 * Format diagnoses list for display (only accepted and active)
 */
export const formatDiagnoses = (diagnoses?: DiagnosisInfo[]): string => {
  if (!diagnoses || diagnoses.length === 0) {
    return 'None.';
  }

  const acceptedDiagnoses = diagnoses.filter(d => d.name && d.status === 'accepted' && d.is_active !== false);

  if (acceptedDiagnoses.length === 0) {
    return 'None.';
  }

  return acceptedDiagnoses
    .map(d => d.name)
    .join(', ');
};

/**
 * Count pending diagnosis suggestions
 */
export const countPendingDiagnosisSuggestions = (diagnoses?: DiagnosisInfo[]): number => {
  if (!diagnoses) return 0;
  return diagnoses.filter(d => d.status === 'suggested').length;
};

/**
 * Format medications list for display (only accepted)
 */
export const formatMedications = (medications?: MedicationInfo[]): string => {
  if (!medications || medications.length === 0) {
    return 'None.';
  }

  const acceptedMedications = medications.filter(m => m.name && m.status === 'accepted');

  if (acceptedMedications.length === 0) {
    return 'None.';
  }

  return acceptedMedications
    .map(m => m.name)
    .join(', ');
};

/**
 * Count pending medication suggestions
 */
export const countPendingMedicationSuggestions = (medications?: MedicationInfo[]): number => {
  if (!medications) return 0;
  return medications.filter(m => m.status === 'suggested').length;
};

/**
 * Format questionnaires count for display
 */
export const formatQuestionnairesCount = (questionnaires?: QuestionnaireInfo[]): string => {
  if (!questionnaires || questionnaires.length === 0) {
    return 'None';
  }

  const count = questionnaires.length;
  return `${count} questionnaire${count !== 1 ? 's' : ''}`;
};

/**
 * Format date for last session display
 */
export const formatSessionDate = (dateString?: string | null): string => {
  if (!dateString) {
    return '';
  }

  try {
    const date =
      typeof dateString === 'string' && dateString.includes('T')
        ? parseISO(dateString)
        : new Date(dateString);

    return format(date, 'MM/dd/yy');
  } catch {
    return '';
  }
};

/**
 * Format questionnaires with detailed information
 * Sorts by date_completed (most recent first)
 */
export const formatQuestionnairesDetail = (
  questionnaires?: QuestionnaireInfo[],
): QuestionnaireInfo[] => {
  if (!questionnaires || questionnaires.length === 0) {
    return [];
  }

  // Sort by date_completed, most recent first
  return [...questionnaires].sort((a, b) => {
    const dateA = a.date_completed ? new Date(a.date_completed).getTime() : 0;
    const dateB = b.date_completed ? new Date(b.date_completed).getTime() : 0;
    return dateB - dateA;
  });
};

/**
 * Get trend symbol for questionnaire score
 */
export const getTrendSymbol = (trend?: string | null): string => {
  switch (trend) {
    case 'up':
      return '↑';
    case 'down':
      return '↓';
    case 'stable':
      return '→';
    default:
      return '';
  }
};

/**
 * Format questionnaire date
 */
export const formatQuestionnaireDate = (dateString?: string | null): string => {
  if (!dateString) {
    return '';
  }

  try {
    const date = parseISO(dateString);
    return format(date, '(MM/dd/yy)');
  } catch {
    return '';
  }
};
